import { Card, Tag, Button, Rate, Tooltip } from 'antd';
import { motion } from 'framer-motion';
import {
  RocketOutlined, VideoCameraOutlined, AimOutlined, EditOutlined,
  SwapOutlined, SoundOutlined, AppstoreOutlined, FacebookOutlined,
  ShoppingOutlined, ClusterOutlined, DashboardOutlined, EyeOutlined,
  LineChartOutlined, TeamOutlined, LikeOutlined, CalculatorOutlined,
  ExperimentOutlined, BellOutlined, MessageOutlined, GlobalOutlined,
  DollarOutlined, SafetyOutlined,
  FireOutlined, DownloadOutlined, CheckCircleOutlined,
  CrownOutlined,
} from '@ant-design/icons';

/**
 * 图标映射
 */
const iconMap = {
  RocketOutlined: RocketOutlined,
  VideoCameraOutlined: VideoCameraOutlined,
  AimOutlined: AimOutlined,
  EditOutlined: EditOutlined,
  SwapOutlined: SwapOutlined,
  SoundOutlined: SoundOutlined,
  AppstoreOutlined: AppstoreOutlined,
  FacebookOutlined: FacebookOutlined,
  ShoppingOutlined: ShoppingOutlined,
  ClusterOutlined: ClusterOutlined,
  DashboardOutlined: DashboardOutlined,
  EyeOutlined: EyeOutlined,
  LineChartOutlined: LineChartOutlined,
  TeamOutlined: TeamOutlined,
  LikeOutlined: LikeOutlined,
  CalculatorOutlined: CalculatorOutlined,
  ExperimentOutlined: ExperimentOutlined,
  BellOutlined: BellOutlined,
  MessageOutlined: MessageOutlined,
  GlobalOutlined: GlobalOutlined,
  DollarOutlined: DollarOutlined,
  SafetyOutlined: SafetyOutlined,
};

/**
 * 分类配色
 */
const categoryColors = {
  featured: '#E8C85A',
  content: '#6366f1',
  advertising: '#f97316',
  analytics: '#10b981',
  influencer: '#ec4899',
  efficiency: '#8b5cf6',
};

/**
 * 插件卡片组件
 */
const PluginCard = ({ plugin, isInstalled, onInstall, onUninstall }) => {
  const IconComponent = iconMap[plugin.icon] || AppstoreOutlined;
  const catColor = categoryColors[plugin.category] || '#6366f1';

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{ height: '100%' }}
    >
      <Card
        hoverable
        style={{
          height: '100%',
          borderRadius: 16,
          border: '1px solid rgba(255, 255, 255, 0.06)',
          background: 'rgba(15, 25, 45, 0.7)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        styles={{
          body: { padding: 20, flex: 1, display: 'flex', flexDirection: 'column' },
        }}
      >
        {/* 头部：图标 + 名称 + HOT徽章 */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'linear-gradient(135deg, ' + catColor + '22, ' + catColor + '44)',
            border: '1px solid ' + catColor + '33',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <IconComponent style={{ fontSize: 22, color: catColor }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                color: 'var(--text-primary)', fontSize: 15, fontWeight: 600,
                fontFamily: 'var(--font-sans)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {plugin.name}
              </span>
              {plugin.isHot && (
                <Tag
                  icon={<FireOutlined />}
                  color="error"
                  style={{ fontSize: 10, padding: '0 4px', lineHeight: '18px', height: 18 }}
                >
                  HOT
                </Tag>
              )}
            </div>
            <div style={{
              fontSize: 11, color: 'var(--text-secondary)', marginTop: 2,
              fontFamily: 'var(--font-sans)',
            }}>
              {plugin.author} {' · '} {plugin.version}
            </div>
          </div>
        </div>

        {/* 描述 */}
        <p style={{
          color: 'rgba(240, 245, 250, 0.6)', fontSize: 12.5, lineHeight: 1.6,
          marginBottom: 12, fontFamily: 'var(--font-sans)',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          flex: 1,
        }}>
          {plugin.description}
        </p>

        {/* 标签 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 14 }}>
          {plugin.tags.slice(0, 3).map((tag) => (
            <Tag
              key={tag}
              style={{
                fontSize: 10, padding: '0 6px', borderRadius: 4,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'rgba(240,245,250,0.5)',
              }}
            >
              {tag}
            </Tag>
          ))}
        </div>

        {/* 评分 + 安装量 */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 14, padding: '8px 0',
          borderTop: '1px solid rgba(255,255,255,0.05)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Rate disabled allowHalf value={plugin.rating} style={{ fontSize: 12 }} />
            <span style={{ color: '#E8C85A', fontSize: 12, fontWeight: 600 }}>{plugin.rating}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'var(--text-secondary)', fontSize: 11 }}>
            <DownloadOutlined style={{ fontSize: 10 }} />
            {plugin.installs >= 1000
              ? (plugin.installs / 1000).toFixed(1) + 'k'
              : plugin.installs}
          </div>
        </div>

        {/* 底部：价格 + 安装按钮 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <Tag
            icon={plugin.priceType === 'free' ? <CheckCircleOutlined /> : <CrownOutlined />}
            color={plugin.priceType === 'free' ? 'success' : 'warning'}
            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, margin: 0 }}
          >
            {plugin.priceType === 'free' ? '免费' : '$' + plugin.price}
          </Tag>

          {isInstalled ? (
            <Tooltip title="点击卸载">
              <Button
                size="small"
                danger
                ghost
                onClick={() => onUninstall && onUninstall(plugin.id)}
                style={{
                  borderRadius: 8, fontSize: 12, height: 28,
                  color: 'rgba(240,245,250,0.4)', borderColor: 'rgba(255,255,255,0.1)',
                }}
              >
                已安装
              </Button>
            </Tooltip>
          ) : (
            <Button
              size="small"
              type="primary"
              onClick={() => onInstall && onInstall(plugin.id)}
              style={{
                borderRadius: 8, fontSize: 12, height: 28,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                border: 'none', boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                paddingLeft: 16, paddingRight: 16,
              }}
            >
              安装
            </Button>
          )}
        </div>
      </Card>
    </motion.div>
  );
};


export default PluginCard;
